import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/common.css';
import logo from '../logo.svg';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const scheduleButtonRef = useRef(null);
  const rankingButtonRef = useRef(null);
  const oshiButtonRef = useRef(null);
  const [isRankingDropdownOpen, setIsRankingDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // ランキングページ用のフィルターステート
  const [selectedPeriod, setSelectedPeriod] = useState('1day');
  const [selectedType, setSelectedType] = useState('views');
  const [selectedPlatform, setSelectedPlatform] = useState('youtube');

  // URLパラメータから現在の値を取得
  useEffect(() => {
    if (location.pathname === '/ranking') {
      const params = new URLSearchParams(location.search);
      const period = params.get('period') || '1day';
      const type = params.get('type') || 'views';
      const platform = params.get('platform') || 'youtube';
      
      setSelectedType(type);
      
      setSelectedPeriod(period);
      setSelectedPlatform(platform);
    }
  }, [location.search, location.pathname]);

  // 現在のランキングプラットフォームを取得
  const getCurrentPlatform = () => {
    if (location.pathname === '/ranking') {
      const params = new URLSearchParams(location.search);
      return params.get('platform') || 'youtube';
    }
    return 'youtube';
  };
  
  // パラメータ変更時にURLを更新
  const updateUrlParams = (period, type, platform) => {
    navigate(`/ranking?period=${period}&type=${type}&platform=${platform}`, { replace: true });
  };

  // ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsRankingDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // アクティブなボタンの位置とサイズを計算
    const updateLinePosition = () => {
      if (!navRef.current) return;

      const activeButton = location.pathname === '/'
        ? scheduleButtonRef.current
        : location.pathname === '/ranking'
        ? rankingButtonRef.current
        : location.pathname.startsWith('/oshi3')
        ? oshiButtonRef.current
        : null;

      if (activeButton) {
        const navRect = navRef.current.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        
        const position = buttonRect.left - navRect.left;
        const width = buttonRect.width;

        navRef.current.style.setProperty('--line-position', `${position}px`);
        navRef.current.style.setProperty('--line-width', `${width}px`);
      } else {
        // プライバシーページなど、どちらもアクティブでない場合は下線を非表示
        navRef.current.style.setProperty('--line-width', '0');
      }
    };

    updateLinePosition();
    
    // ウィンドウリサイズ時にも位置を更新
    window.addEventListener('resize', updateLinePosition);
    return () => window.removeEventListener('resize', updateLinePosition);
  }, [location.pathname]);

  // ランキングボタンのクリックハンドラー
  const handleRankingClick = (e) => {
    e.preventDefault();
    setIsRankingDropdownOpen(!isRankingDropdownOpen);
  };

  // プラットフォーム選択ハンドラー
  const handlePlatformSelect = (platform) => {
    // スケジュールページからの遷移の場合はデフォルト値を使用
    if (location.pathname !== '/ranking') {
      navigate(`/ranking?period=1day&type=views&platform=${platform}`);
    } else {
      // ランキングページからの切り替えは現在のパラメータを維持
      const params = new URLSearchParams(location.search);
      const currentPeriod = params.get('period') || '1day';
      const currentType = params.get('type') || 'views';
      navigate(`/ranking?period=${currentPeriod}&type=${currentType}&platform=${platform}`);
    }
    setIsRankingDropdownOpen(false);
  };

  const currentPlatform = getCurrentPlatform();

  return (
    <header className="header">
      <div className="header-inner">
        <Link to="/" className="logo-container">
          <img src={logo} alt="STPR" className="logo" />
          <span className="alpha-badge">α版</span>
        </Link>
        <nav className="header-nav" ref={navRef}>
          <Link 
            to="/" 
            ref={scheduleButtonRef}
            className={`nav-button ${location.pathname === '/' ? 'active' : ''}`}
          >
            配信スケジュール
          </Link>
          <Link
            to="/oshi3"
            ref={oshiButtonRef}
            className={`nav-button nav-button-oshi ${location.pathname.startsWith('/oshi3') ? 'active' : ''}`}
          >
            推しメン3
          </Link>
          <div className="ranking-nav-wrapper" ref={dropdownRef}>
            <Link 
              to="/ranking" 
              ref={rankingButtonRef}
              className={`nav-button ${location.pathname === '/ranking' ? 'active' : ''}`}
              onClick={handleRankingClick}
            >
              ランキング
              <span className="dropdown-arrow">▼</span>
            </Link>
            {isRankingDropdownOpen && (
              <div className="ranking-dropdown-menu">
                <button 
                  className={`dropdown-item ${location.pathname === '/ranking' && currentPlatform === 'youtube' ? 'active' : ''}`}
                  onClick={() => handlePlatformSelect('youtube')}
                >
                  YouTube
                </button>
                <button 
                  className={`dropdown-item ${location.pathname === '/ranking' && currentPlatform === 'twitcasting' ? 'active' : ''}`}
                  onClick={() => handlePlatformSelect('twitcasting')}
                >
                  ツイキャス
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
      
      {/* ランキングページ専用フィルター */}
      {location.pathname === '/ranking' && (
        <div className="ranking-page-header">
          <div className="ranking-filters">
            {/* プラットフォーム選択ドロップダウン */}
            <select 
              name="platform"
              className="ranking-dropdown"
              value={selectedPlatform}
              onChange={(e) => {
                const newPlatform = e.target.value;
                setSelectedPlatform(newPlatform);
                updateUrlParams(selectedPeriod, selectedType, newPlatform);
              }}
            >
              <option value="youtube">YouTube</option>
              <option value="twitcasting">ツイキャス</option>
            </select>
            
            {/* 期間選択ドロップダウン */}
            <select 
              name="period"
              className="ranking-dropdown"
              value={selectedPeriod}
              onChange={(e) => {
                setSelectedPeriod(e.target.value);
                updateUrlParams(e.target.value, selectedType, selectedPlatform);
              }}
            >
              <option value="1day">昨日</option>
              <option value="7day">7日間</option>
            </select>
            
            {/* タイプ選択ドロップダウン */}
            <select
              name="type"
              className="ranking-dropdown"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                updateUrlParams(selectedPeriod, e.target.value, selectedPlatform);
              }}
            >
              <option value="concurrent">
                同接数
              </option>
              <option value="views">{selectedPlatform === 'twitcasting' ? '視聴者数' : '再生数'}</option>
            </select>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
